import { useCallback } from 'react';
import { useTabs } from '../../../../context/TabsContext';

/**
 * URL-driven navigation callbacks — invoked when the URL changes
 * (back/forward, direct URL entry). Updates app state without changing URL.
 */
export function useUrlNavigateCallbacks({
  isInitialStartupComplete,
  sprints,
  handleSelectSprint,
  handleSelectTemplate,
  handleSelectView,
  handleSidebarNavigate,
  openDocsArea,
  openDashboardsArea,
  openCampaignsHomeArea,
  openSettingsModal,
  setPreferredDetailView,
  setPendingMissionFocus,
  getFirstActiveSprint,
  syncTabToContent,
  handleTabViewModeChange,
  handleTabClick,
}) {
  const { activeTab, tabs, switchToTab, showInActiveTab, openDocsHomeTab, openDashboardsHomeTab } = useTabs();

  const handleUrlNavigateToCampaign = useCallback(async (campaignId, options = {}) => {
    console.log('[handleUrlNavigateToCampaign] Called with:', { campaignId, options });

    if (!isInitialStartupComplete) {
      console.log('[handleUrlNavigateToCampaign] Not ready yet (initial startup incomplete)');
      return;
    }

    const { viewMode, missionId, customViewId } = options;

    const campaign = sprints.find(s => s.id === campaignId && !s.isTemplate);
    if (!campaign) {
      console.warn('[App] Campaign not found for URL navigation:', campaignId);
      return;
    }

    const selectOptions = {};
    if (viewMode) {
      selectOptions.view = customViewId
        ? { customViewId, type: viewMode, skipUrlUpdate: true }
        : { type: viewMode, skipUrlUpdate: true };
    }
    if (missionId) {
      selectOptions.missionId = missionId;
    }

    console.log('[handleUrlNavigateToCampaign] Calling handleSelectSprint with:', selectOptions);

    syncTabToContent?.('campaign', campaign.id, campaign.name, campaign.icon, viewMode);
    await handleSelectSprint(campaign, selectOptions);
  }, [isInitialStartupComplete, sprints, handleSelectSprint, syncTabToContent]);

  const handleUrlNavigateToTemplate = useCallback(async (templateId, options = {}) => {
    if (!isInitialStartupComplete) return;

    const { viewMode } = options;

    const template = sprints.find(s => s.id === templateId && (s.isTemplate || s.type === 'template'));
    if (!template) {
      console.warn('[App] Template not found for URL navigation:', templateId);
      return;
    }

    if (viewMode) {
      setPreferredDetailView(viewMode);
    }

    syncTabToContent?.('template', template.id, template.name, template.icon || '📋', viewMode);
    await handleSelectTemplate(template);
  }, [isInitialStartupComplete, sprints, handleSelectTemplate, setPreferredDetailView, syncTabToContent]);

  const handleUrlNavigateToView = useCallback(async (viewId, options = {}) => {
    if (!isInitialStartupComplete) return;

    const { viewMode } = options;

    if (viewMode) {
      setPreferredDetailView(viewMode);
    }

    syncTabToContent?.('view', viewId, 'View', '📊', viewMode);
    await handleSelectView({ id: viewId });
  }, [isInitialStartupComplete, handleSelectView, setPreferredDetailView, syncTabToContent]);

  const handleUrlNavigateToSettings = useCallback(() => {
    if (!isInitialStartupComplete) return;
    // Overlay only — leave the current tab owning the main view.
    openSettingsModal?.();
  }, [isInitialStartupComplete, openSettingsModal]);

  const handleUrlNavigateToBlank = useCallback(() => {
    if (!isInitialStartupComplete) return;
    if (activeTab?.type === 'blank') {
      handleTabClick?.(activeTab);
      return;
    }
    const blank = tabs.find((t) => t.type === 'blank');
    if (blank) {
      switchToTab(blank.id);
      handleTabClick?.(blank);
      return;
    }
    showInActiveTab({
      type: 'blank',
      targetId: null,
      label: 'New Tab',
      icon: null,
      viewMode: null,
    });
    handleTabClick?.({ type: 'blank', targetId: null, label: 'New Tab', icon: null });
  }, [isInitialStartupComplete, activeTab, tabs, switchToTab, showInActiveTab, handleTabClick]);

  const handleUrlNavigateToDocs = useCallback(() => {
    if (!isInitialStartupComplete) return;
    openDocsArea?.();
    // Same URL as an open document — don't steal that tab.
    if (activeTab?.type !== 'doc') {
      openDocsHomeTab();
    }
  }, [isInitialStartupComplete, openDocsArea, activeTab?.type, openDocsHomeTab]);

  const handleUrlNavigateToDashboards = useCallback(() => {
    if (!isInitialStartupComplete) return;
    openDashboardsArea?.();
    openDashboardsHomeTab();
  }, [isInitialStartupComplete, openDashboardsArea, openDashboardsHomeTab]);

  const handleUrlNavigateToCampaigns = useCallback(() => {
    if (!isInitialStartupComplete) return;
    openCampaignsHomeArea?.();
  }, [isInitialStartupComplete, openCampaignsHomeArea]);

  const handleUrlNavigateToHome = useCallback(async () => {
    if (!isInitialStartupComplete) return;

    const firstCampaign = getFirstActiveSprint();
    if (firstCampaign) {
      syncTabToContent?.('campaign', firstCampaign.id, firstCampaign.name, firstCampaign.icon, null);
      await handleSelectSprint(firstCampaign);
      return;
    }
    openCampaignsHomeArea?.();
  }, [isInitialStartupComplete, getFirstActiveSprint, handleSelectSprint, syncTabToContent, openCampaignsHomeArea]);

  const handleUrlViewModeChange = useCallback((viewMode, customViewId, missionId) => {
    if (!isInitialStartupComplete) return;

    handleTabViewModeChange(viewMode, customViewId);

    setPreferredDetailView(customViewId
      ? { customViewId, type: viewMode }
      : viewMode);

    if (missionId) {
      setPendingMissionFocus(missionId);
    }
  }, [isInitialStartupComplete, handleTabViewModeChange, setPreferredDetailView, setPendingMissionFocus]);

  return {
    handleUrlNavigateToCampaign,
    handleUrlNavigateToTemplate,
    handleUrlNavigateToView,
    handleUrlNavigateToSettings,
    handleUrlNavigateToBlank,
    handleUrlNavigateToDocs,
    handleUrlNavigateToDashboards,
    handleUrlNavigateToCampaigns,
    handleUrlNavigateToHome,
    handleUrlViewModeChange,
  };
}
